import { useState, useRef, useCallback } from 'react';
import useOnTaskIdentity from './useOnTaskIdentity';

/**
 * Hook for managing the connection menu (add connected node)
 */
export function useConnectionMenu({ taskId, onCreateConnectedNode }) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const handleRef = useRef(null);
  /** When adding from a decision branch, the option id; otherwise null */
  const menuBranchRef = useRef(null);

  useOnTaskIdentity(taskId, () => {
    menuBranchRef.current = null;
    setShowMenu(false);
    setMenuPosition(null);
  });

  const openMenu = useCallback((e, branchOptionId = null) => {
    e.stopPropagation();
    e.preventDefault();
    menuBranchRef.current = branchOptionId;

    if (handleRef.current) {
      const rect = handleRef.current.getBoundingClientRect();
      setMenuPosition({
        x: rect.left + rect.width / 2,
        y: rect.bottom,
      });
    }
    setShowMenu(true);
  }, []);

  /** Open menu at a fixed screen position (e.g. decision branch pills) */
  const openMenuFromPosition = useCallback((position, branchOptionId = null) => {
    menuBranchRef.current = branchOptionId;
    setMenuPosition({
      x: position.x,
      y: position.y,
    });
    setShowMenu(true);
  }, []);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setMenuPosition(null);
    menuBranchRef.current = null;
  }, []);

  const selectNodeType = useCallback((nodeType) => {
    const branch = menuBranchRef.current;
    menuBranchRef.current = null;
    setShowMenu(false);
    setMenuPosition(null);
    onCreateConnectedNode?.(taskId, nodeType, branch);
  }, [taskId, onCreateConnectedNode]);

  return {
    handleRef,
    showMenu,
    menuPosition,
    openMenu,
    openMenuFromPosition,
    closeMenu,
    selectNodeType,
  };
}

export default useConnectionMenu;

